import { useEffect, useState } from "react";
import { PageHeader } from "../App";
import { 
  Brain, 
  Play,
  Pause,
  CheckCircle,
  XCircle,
  Clock,
  Download,
  Plus,
  Trash,
  Spinner,
  FileText,
  Database,
  Lightning,
  ArrowRight
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import api from "../lib/api";

const StatusBadge = ({ status }) => {
  const configs = {
    pending: { bg: 'bg-zinc-500/10', text: 'text-zinc-400', icon: Clock },
    preparing: { bg: 'bg-blue-500/10', text: 'text-blue-400', icon: Spinner },
    prepared: { bg: 'bg-cyan-500/10', text: 'text-cyan-400', icon: FileText },
    training: { bg: 'bg-amber-500/10', text: 'text-amber-400', icon: Lightning },
    completed: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', icon: CheckCircle },
    failed: { bg: 'bg-red-500/10', text: 'text-red-400', icon: XCircle },
  };
  
  const config = configs[status] || configs.pending;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon weight={status === 'preparing' || status === 'training' ? 'bold' : 'fill'} 
            className={`w-3.5 h-3.5 ${status === 'preparing' || status === 'training' ? 'animate-spin' : ''}`} />
      <span className="capitalize">{status}</span>
    </span>
  );
};

const TrainingRunCard = ({ run, onPrepare, onStart, onDownload, onDelete }) => {
  const metrics = run.metrics || {};
  
  return (
    <div className="border border-zinc-800 rounded-lg p-4 hover:border-zinc-700 transition-colors">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="font-medium text-zinc-100">{run.name}</h4>
          <p className="text-xs text-zinc-500 mt-0.5">
            Base: {run.base_model} • {run.conversation_ids?.length || 0} conversations
          </p>
        </div>
        <StatusBadge status={run.status} />
      </div>
      
      {run.description && (
        <p className="text-sm text-zinc-400 mb-3">{run.description}</p>
      )}
      
      {/* Metrics */}
      {metrics.total_examples && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="p-2 bg-zinc-900/50 rounded">
            <p className="text-xs text-zinc-500">Examples</p>
            <p className="font-mono text-zinc-200">{metrics.total_examples}</p>
          </div>
          <div className="p-2 bg-zinc-900/50 rounded">
            <p className="text-xs text-zinc-500">Conversations</p>
            <p className="font-mono text-zinc-200">{metrics.total_conversations}</p>
          </div>
          <div className="p-2 bg-zinc-900/50 rounded">
            <p className="text-xs text-zinc-500">File Size</p>
            <p className="font-mono text-zinc-200">
              {(metrics.file_size_bytes / 1024).toFixed(1)} KB
            </p>
          </div>
        </div>
      )}
      
      {metrics.model_name && (
        <div className="p-2 bg-emerald-500/10 border border-emerald-500/30 rounded mb-3">
          <p className="text-xs text-emerald-400">
            Model created: <span className="font-mono">{metrics.model_name}</span>
          </p>
        </div>
      )}
      
      {run.error && (
        <div className="p-2 bg-red-500/10 border border-red-500/30 rounded mb-3">
          <p className="text-xs text-red-400">{run.error}</p>
        </div>
      )}
      
      {/* Actions */}
      <div className="flex items-center gap-2 pt-3 border-t border-zinc-800">
        {run.status === 'pending' && (
          <Button size="sm" onClick={() => onPrepare(run.id)}>
            <FileText weight="bold" className="w-4 h-4 mr-1" />
            Prepare Data
          </Button>
        )}
        
        {run.status === 'prepared' && (
          <>
            <Button size="sm" onClick={() => onStart(run.id)}>
              <Play weight="fill" className="w-4 h-4 mr-1" />
              Start Training
            </Button>
            <Button size="sm" variant="outline" onClick={() => onDownload(run.id)}>
              <Download weight="bold" className="w-4 h-4" />
            </Button>
          </>
        )}
        
        {run.status === 'completed' && run.training_file && (
          <Button size="sm" variant="outline" onClick={() => onDownload(run.id)}>
            <Download weight="bold" className="w-4 h-4 mr-1" />
            Download Data
          </Button>
        )}
        
        <Button 
          size="sm" 
          variant="ghost" 
          className="ml-auto text-zinc-500 hover:text-red-400"
          onClick={() => onDelete(run.id)}
        >
          <Trash weight="regular" className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

const CreateRunDialog = ({ datasets, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    base_model: 'llama3.2',
    dataset_ids: [],
    export_format: 'jsonl',
    min_quality_score: null
  });

  const handleCreate = async () => {
    if (!form.name) {
      toast.error("Please enter a name");
      return;
    }
    
    setLoading(true);
    try {
      await api.createTrainingRun(form);
      toast.success("Training run created");
      setOpen(false);
      setForm({
        name: '',
        description: '',
        base_model: 'llama3.2',
        dataset_ids: [],
        export_format: 'jsonl',
        min_quality_score: null
      });
      onCreated();
    } catch (error) {
      toast.error("Failed to create training run");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="create-training-run-btn">
          <Plus weight="bold" className="w-4 h-4 mr-2" />
          New Training Run
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Training Run</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input
              placeholder="My Custom Model v1"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="training-run-name"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Description (optional)</Label>
            <Textarea
              placeholder="Training on tech support conversations..."
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Base Model</Label>
              <Select value={form.base_model} onValueChange={(v) => setForm({ ...form, base_model: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="llama3.2">Llama 3.2</SelectItem>
                  <SelectItem value="llama3.1">Llama 3.1</SelectItem>
                  <SelectItem value="mistral">Mistral</SelectItem>
                  <SelectItem value="phi3">Phi-3</SelectItem>
                  <SelectItem value="gemma2">Gemma 2</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Export Format</Label>
              <Select value={form.export_format} onValueChange={(v) => setForm({ ...form, export_format: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="jsonl">JSONL</SelectItem>
                  <SelectItem value="alpaca">Alpaca</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Datasets</Label>
            <ScrollArea className="h-32 border border-zinc-800 rounded-md p-2">
              <div className="flex flex-wrap gap-2">
                {datasets.map(d => (
                  <button
                    key={d.id}
                    onClick={() => {
                      const ids = form.dataset_ids.includes(d.id)
                        ? form.dataset_ids.filter(id => id !== d.id)
                        : [...form.dataset_ids, d.id];
                      setForm({ ...form, dataset_ids: ids });
                    }}
                    className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                      form.dataset_ids.includes(d.id)
                        ? 'border-blue-500 bg-blue-500/10 text-blue-300'
                        : 'border-zinc-700 text-zinc-400 hover:border-zinc-600'
                    }`}
                  >
                    {d.name}
                  </button>
                ))}
              </div>
              {datasets.length === 0 && (
                <p className="text-sm text-zinc-500">No datasets available</p>
              )}
            </ScrollArea>
          </div>
          
          <div className="space-y-2">
            <Label>Min Quality Score (optional)</Label>
            <Input
              type="number"
              min="0"
              max="10"
              step="0.5"
              placeholder="Leave empty to include all"
              value={form.min_quality_score || ''}
              onChange={(e) => setForm({ ...form, min_quality_score: e.target.value ? parseFloat(e.target.value) : null })}
            />
            <p className="text-xs text-zinc-500">Only include conversations with this score or higher</p>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} disabled={loading}>
            {loading ? <Spinner className="w-4 h-4 mr-2 animate-spin" /> : null}
            Create Run
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Training() {
  const [runs, setRuns] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [runsData, datasetsData, statsData] = await Promise.all([
        api.getTrainingRuns(),
        api.getDatasets(),
        api.getTrainingStats()
      ]);
      setRuns(runsData);
      setDatasets(datasetsData);
      setStats(statsData);
    } catch (error) {
      console.error("Failed to load data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrepare = async (runId) => {
    try {
      toast.info("Preparing training data...");
      await api.prepareTrainingData(runId);
      toast.success("Training data prepared");
      loadData();
    } catch (error) {
      toast.error("Failed to prepare data");
    }
  };

  const handleStart = async (runId) => {
    try {
      toast.info("Starting training...");
      const result = await api.startTraining(runId);
      toast.success(result.message || "Training completed");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Training failed");
      loadData();
    }
  };

  const handleDownload = async (runId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/training/runs/${runId}/download`);
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `training_${runId}.jsonl`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success("File downloaded successfully");
    } catch (error) {
      console.error('Download error:', error);
      toast.error("Failed to download file");
    }
  };

  const handleDelete = async (runId) => {
    if (!confirm("Delete this training run?")) return;
    
    try {
      await api.deleteTrainingRun(runId);
      toast.success("Training run deleted");
      loadData();
    } catch (error) {
      toast.error("Failed to delete run");
    }
  };

  return (
    <div>
      <PageHeader 
        title="Training" 
        subtitle="Manage and track AI model training runs"
      />
      
      <div className="p-8 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className="stat-card">
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Total Runs</p>
            <p className="text-2xl font-mono text-zinc-100 mt-1">{stats.total_runs || 0}</p>
          </div>
          <div className="stat-card border-emerald-500/30">
            <p className="text-xs text-emerald-400 uppercase tracking-wider">Completed</p>
            <p className="text-2xl font-mono text-emerald-400 mt-1">{stats.completed_runs || 0}</p>
          </div>
          <div className="stat-card border-amber-500/30">
            <p className="text-xs text-amber-400 uppercase tracking-wider">Pending</p>
            <p className="text-2xl font-mono text-amber-400 mt-1">{stats.pending_runs || 0}</p>
          </div>
          <div className="stat-card border-blue-500/30">
            <p className="text-xs text-blue-400 uppercase tracking-wider">Total Trained</p>
            <p className="text-2xl font-mono text-blue-400 mt-1">{stats.total_conversations_trained || 0}</p>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider">
            Training Runs
          </h3>
          <CreateRunDialog datasets={datasets} onCreated={loadData} />
        </div>

        {/* Runs List */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-48 bg-zinc-800 rounded-lg animate-pulse"></div>
            ))}
          </div>
        ) : runs.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {runs.map(run => (
              <TrainingRunCard
                key={run.id}
                run={run}
                onPrepare={handlePrepare}
                onStart={handleStart}
                onDownload={handleDownload}
                onDelete={handleDelete}
              />
            ))}
          </div>
        ) : (
          <div className="chart-container py-12 text-center">
            <Brain weight="thin" className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400 mb-2">No training runs yet</p>
            <p className="text-sm text-zinc-500 mb-4">
              Create a training run to start building your custom AI model
            </p>
            <CreateRunDialog datasets={datasets} onCreated={loadData} />
          </div>
        )}

        {/* Training Workflow */}
        <div className="chart-container">
          <h3 className="text-sm font-medium text-zinc-400 mb-4 uppercase tracking-wider">
            Training Workflow
          </h3>
          <div className="flex items-center justify-between">
            {[
              { num: 1, label: 'Create Run', desc: 'Select datasets & config' },
              { num: 2, label: 'Prepare Data', desc: 'Generate training file' },
              { num: 3, label: 'Start Training', desc: 'Create model with Ollama' },
              { num: 4, label: 'Use Model', desc: 'Run your custom AI' },
            ].map((step, idx) => (
              <div key={step.num} className="flex items-center">
                <div className="flex flex-col items-center text-center">
                  <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center mb-2">
                    <span className="text-blue-400 font-mono">{step.num}</span>
                  </div>
                  <p className="text-sm text-zinc-200">{step.label}</p>
                  <p className="text-xs text-zinc-500">{step.desc}</p>
                </div>
                {idx < 3 && (
                  <ArrowRight weight="bold" className="w-5 h-5 text-zinc-600 mx-4" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
